import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import GameStatSlider from "./GameStatSlider";
import { getSelectedSport } from "../utils/helpers";
import { Pagination, Switch, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./design-system";

const TeamRecord = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    wins: 0,
    losses: 0,
    homeWins: 0,
    homeLosses: 0,
    awayWins: 0,
    awayLosses: 0,
    conferenceWins: 0,
    conferenceLosses: 0,
    nonConferenceWins: 0,
    nonConferenceLosses: 0,
    streakCount: 0,
    streakType: "",
    lastFiveGames: [],
    upcomingGames: [],
  });
  const [activeTab, setActiveTab] = useState("all");
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [isStatSliderOpen, setIsStatSliderOpen] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalTeams, setTotalTeams] = useState(0);
  const [teamStats, setTeamStats] = useState({
    overall_record: "",
    overall_win_percentage: 0,
    conference_record: "",
    conference_win_percentage: 0,
    current_streak: "",
    home_record: "",
    away_record: "",
  });
  const [selectedYear, setSelectedYear] = useState(null);
  const [showConference, setShowConference] = useState(false);
  const [lastFiveGame, setLastFiveGame] = useState([]);

  const yearOptions = [
    { value: null, label: "All Seasons" },
    ...Array.from({ length: 8 }, (_, i) => ({
      value: 2025 - i,
      label: `${2025 - i} Season`,
    })),
  ];

  useEffect(() => {
    // Try to get team from session storage
    const savedTeam = sessionStorage.getItem("selectedTeam");
    if (savedTeam) {
      setTeam(JSON.parse(savedTeam));
    }

    // Fetch games data for team
    fetchTeamGames(1, pageSize);
    getTeamsStats(false);
    fetchLastFiveTeamGames();
    // Add event listener for game navigation from the slider
    const handleGameNavigation = (event) => {
      const { gameId } = event.detail;
      if (gameId) {
        setSelectedGameId(gameId);
      }
    };

    window.addEventListener("gameNavigation", handleGameNavigation);

    // Cleanup
    return () => {
      window.removeEventListener("gameNavigation", handleGameNavigation);
    };
  }, [teamId]);

  const fetchTeamGames = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      // Use the URL parameter or the team from session storage
      const idToUse = teamId || team?.teamID || team?.team_id;

      if (!idToUse) {
        setError("No team ID provided");
        setLoading(false);
        return;
      }
      const skip = (page - 1) * size;

      const response = await apiClient.get(
        `/${getSelectedSport()}/games/?skip=${skip}&limit=${size}&team_id=${idToUse}&schema=${getSelectedSport()}`
      );

      // Check if response is an array (new format) or has an items property (old format)
      const gamesData = Array.isArray(response?.data?.data)
        ? response?.data?.data
        : response?.data?.data && response.data.items
        ? response.data.items
        : [];

      // Sort games by date (newest first)
      gamesData.sort((a, b) => new Date(b.GameDay) - new Date(a.GameDay));

      setGames(gamesData);
      setTotalTeams(response?.data?.pagination?.total_count);
      calculateStats(gamesData, idToUse);
    } catch (error) {
      console.error("Error fetching team games:", error);
      setError(`Failed to load team games: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastFiveTeamGames = async (page = 1, size = 5) => {
    setLoading(true);
    try {
      // Use the URL parameter or the team from session storage
      const idToUse = teamId || team?.teamID || team?.team_id;

      if (!idToUse) {
        setError("No team ID provided");
        setLoading(false);
        return;
      }
      const skip = (page - 1) * size;

      console.log(`Fetching games for team ID: ${idToUse}`);
      const response = await apiClient.get(
        `/${getSelectedSport()}/games/?skip=${skip}&limit=${size}&team_id=${idToUse}&schema=${getSelectedSport()}`
      );

      // Check if response is an array (new format) or has an items property (old format)
      const gamesData = Array.isArray(response?.data?.data)
        ? response?.data?.data
        : response?.data?.data && response.data.items
        ? response.data.items
        : [];

      let lastFiveGames = [];
      // Sort games by date (newest first)
      for (let i = 0; i < gamesData?.length; i++) {
        const game = gamesData[i];
        const isHome = parseInt(game.HomeID) === parseInt(teamId);
        const teamScore = isHome ? game.ScoreHome : game.ScoreVis;
        const opponentScore = isHome ? game.ScoreVis : game.ScoreHome;
        const isWin = teamScore > opponentScore;

        lastFiveGames.push({
          gameId: game.ID || game.GameID,
          opponent: isHome ? game.Visitor : game.Home,
          opponentAbbrev: isHome
            ? game.VisitorAbbrev || game.TeamAbbrev
            : game.HomeAbbrev || game.TeamAbbrev,
          isHome,
          isWin,
          teamScore,
          opponentScore,
          date: game.GameDay,
          conference: game.Conference,
        });
      }

      setLastFiveGame(lastFiveGames);
    } catch (error) {
      console.error("Error fetching team games:", error);
      setError(`Failed to load team games: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getTeamsStats = async (conference_only = false, season_year = null) => {
    try {
      const idToUse = teamId || team?.teamID || team?.team_id;
      if (!idToUse) {
        setError("No team ID provided");
        setLoading(false);
        return;
      }

      // Build up the params object dynamically
      const params = {
        conference_only,
        schema: getSelectedSport(),
      };
      if (season_year) {
        params.season_year = season_year;
      }

      const url = `${getSelectedSport()}/games/team-stats/${idToUse}`;
      const response = await apiClient.get(url, { params });

      // Drill down to the data you want
      setTeamStats(response.data.statistics);
    } catch (error) {
      console.error("Error fetching team games:", error);
      setError(`Failed to load team games: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (gamesData, teamId) => {
    let wins = 0,
      losses = 0;
    let homeWins = 0,
      homeLosses = 0;
    let awayWins = 0,
      awayLosses = 0;
    let conferenceWins = 0,
      conferenceLosses = 0;
    let nonConferenceWins = 0,
      nonConferenceLosses = 0;
    let lastFiveGames = [];
    let upcomingGames = [];

    // Calculate streak
    let streakCount = 0;
    let streakType = "";
    let lastResult = null;

    // Process completed games for stats
    const completedGames = gamesData.filter(
      (game) => game.GameStatus === "Final"
    );
    const sortedCompletedGames = [...completedGames].sort(
      (a, b) => new Date(a.GameDay) - new Date(b.GameDay)
    );

    sortedCompletedGames.forEach((game) => {
      const isHome = parseInt(game.HomeID) === parseInt(teamId);
      const isVisitor = parseInt(game.VisitorID) === parseInt(teamId);
      const teamScore = isHome ? game.ScoreHome : game.ScoreVis;
      const opponentScore = isHome ? game.ScoreVis : game.ScoreHome;
      const isWin = teamScore > opponentScore;
      const isConference = game.Conference && game.Conference !== "";

      // Calculate streak
      if (lastResult === null) {
        lastResult = isWin;
        streakCount = 1;
        streakType = isWin ? "W" : "L";
      } else if (lastResult === isWin) {
        streakCount++;
      } else {
        lastResult = isWin;
        streakCount = 1;
        streakType = isWin ? "W" : "L";
      }

      if (isWin) {
        wins++;
        if (isHome) homeWins++;
        if (isVisitor) awayWins++;
        if (isConference) conferenceWins++;
        else nonConferenceWins++;
      } else {
        losses++;
        if (isHome) homeLosses++;
        if (isVisitor) awayLosses++;
        if (isConference) conferenceLosses++;
        else nonConferenceLosses++;
      }
    });

    // Get last 5 games (most recent first)
    const recentCompletedGames = [...completedGames].sort(
      (a, b) => new Date(b.GameDay) - new Date(a.GameDay)
    );

    for (let i = 0; i < Math.min(5, recentCompletedGames.length); i++) {
      const game = recentCompletedGames[i];
      const isHome = parseInt(game.HomeID) === parseInt(teamId);
      const teamScore = isHome ? game.ScoreHome : game.ScoreVis;
      const opponentScore = isHome ? game.ScoreVis : game.ScoreHome;
      const isWin = teamScore > opponentScore;

      lastFiveGames.push({
        gameId: game.ID || game.GameID,
        opponent: isHome ? game.Visitor : game.Home,
        opponentAbbrev: isHome
          ? game.VisitorAbbrev || game.TeamAbbrev
          : game.HomeAbbrev || game.TeamAbbrev,
        isHome,
        isWin,
        teamScore,
        opponentScore,
        date: game.GameDay,
        conference: game.Conference,
      });
    }

    // Get upcoming games (future dates)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const futureGames = gamesData
      .filter((game) => {
        const gameDate = new Date(game.GameDay);
        gameDate.setHours(0, 0, 0, 0);
        return gameDate >= today && game.GameStatus !== "Final";
      })
      .sort((a, b) => new Date(a.GameDay) - new Date(b.GameDay));

    for (let i = 0; i < Math.min(3, futureGames.length); i++) {
      const game = futureGames[i];
      const isHome = parseInt(game.HomeID) === parseInt(teamId);

      upcomingGames.push({
        gameId: game.ID || game.GameID,
        opponent: isHome ? game.Visitor : game.Home,
        opponentAbbrev: isHome
          ? game.VisitorAbbrev || game.TeamAbbrev
          : game.HomeAbbrev || game.TeamAbbrev,
        isHome,
        date: game.GameDay,
        time: game.GameTime,
        venue: game.Venue,
        conference: game.Conference,
      });
    }

    setStats({
      wins,
      losses,
      homeWins,
      homeLosses,
      awayWins,
      awayLosses,
      conferenceWins,
      conferenceLosses,
      nonConferenceWins,
      nonConferenceLosses,
      streakCount,
      streakType,
      lastFiveGames,
      upcomingGames,
    });
  };

  const formatDate = (dateString) => {
    const options = { weekday: "short", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getTeamColor = () => {
    if (team && team.team_color) {
      return team.team_color;
    }
    return "#3b82f6"; // Default to blue if no team color
  };

  const filteredGames = () => {
    if (activeTab === "all") {
      return games;
    } else if (activeTab === "home") {
      return games.filter(
        (game) =>
          parseInt(game.HomeID) ===
          parseInt(teamId || team?.teamID || team?.team_id)
      );
    } else if (activeTab === "away") {
      return games.filter(
        (game) =>
          parseInt(game.VisitorID) ===
          parseInt(teamId || team?.teamID || team?.team_id)
      );
    } else if (activeTab === "conference") {
      return games.filter((game) => game.Conference && game.Conference !== "");
    } else if (activeTab === "completed") {
      return games.filter((game) => game.GameStatus === "Final");
    } else if (activeTab === "upcoming") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return games.filter((game) => {
        const gameDate = new Date(game.GameDay);
        gameDate.setHours(0, 0, 0, 0);
        return gameDate >= today;
      });
    }
    return games;
  };

  const teamPrimaryColor = getTeamColor();
  const teamSecondaryColor =
    teamPrimaryColor === "#3b82f6" ? "#2563eb" : teamPrimaryColor;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#E8B800]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="h-8 w-8 text-[#E8B800]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
            </svg>
          </div>
        </div>
        <p className="ml-4 text-xl font-semibold text-[#999999]">
          Loading team data...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="max-w-4xl mx-auto mt-8 p-6 bg-[#111111] rounded-[14px] shadow-lg"
      >
        <div className="flex items-center mb-6 text-red-600">
          <svg className="h-8 w-8 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            ></path>
          </svg>
          <h2 className="text-2xl font-bold text-[#F5F5F5]">Error Loading Team Record</h2>
        </div>
        <p
          className="text-[#999999] mb-4"
        >
          {error}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 themeSecondary text-white rounded-md hoverintoGray transition-colors shadow-md"
        >
          Return to Previous Page
        </button>
      </div>
    );
  }

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchTeamGames(page, size);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl bg-[#0A0A0A]">
      {/* Hero Section */}

      <div className="mb-6 flex justify-end items-center">
        <div className="flex items-center space-x-2">
          <span className="text-[#999999]">Conference</span>
          <Switch
            checked={showConference}
            onChange={(checked) => {
              setShowConference(checked);
              getTeamsStats(checked, selectedYear);
            }}
          />
        </div>
        <div className="ml-3 relative w-full max-w-[200px]">
          <Select
            value={selectedYear === null ? "all" : String(selectedYear)}
            onValueChange={(val) => {
              const newValue = val === "all" ? null : Number(val);
              setSelectedYear(newValue);
              getTeamsStats(showConference, newValue);
            }}
          >
            <SelectTrigger
              className="w-full bg-[#111111] border-[#1A1A1A] text-[#999999] focus:ring-[#E8B800] focus:border-[#E8B800]"
            >
              <SelectValue placeholder="All Seasons" />
            </SelectTrigger>
            <SelectContent className="bg-[#111111] border-[#1A1A1A]">
              {yearOptions.map((year) => (
                <SelectItem
                  key={year.value === null ? "all" : year.value}
                  value={year.value === null ? "all" : String(year.value)}
                  className="text-[#999999] focus:bg-[rgba(255,255,255,0.04)]"
                >
                  {year.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div
        className="mb-8 rounded-2xl shadow-xl overflow-hidden"
        style={{
          background: `linear-gradient(to right, #1e293b, #334155)`,
        }}
      >
        <div className="px-6 py-12 sm:px-10 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-6 flex items-center transition-colors text-[#999999] hover:text-[#F5F5F5]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back
          </button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center">
                {team && team.logo_url && (
                  <img
                    src={team.logo_url}
                    alt={team?.full_name || team?.team_name || "Team Logo"}
                    className="h-20 w-20 object-contain bg-[#111111] rounded-full p-2 mr-4 shadow-lg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/SLAM_logo.png";
                    }}
                  />
                )}
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold leading-tight text-[#F5F5F5]">
                    {team ? team.full_name || team.team_name : "Team"}
                  </h1>
                  <p className="text-lg mt-1 text-[#999999]">
                    {team?.conference ? team.conference : ""}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-center">
              <div className="bg-[rgba(0,0,0,0.4)] backdrop-blur-sm rounded-[14px] px-6 py-3 min-w-[120px]">
                <div className="text-3xl font-bold text-white">
                  {teamStats?.overall_record}
                </div>
                <div className="text-[#999999] mt-1">Overall</div>
              </div>

              <div className="bg-[rgba(0,0,0,0.4)] backdrop-blur-sm rounded-[14px] px-6 py-3 min-w-[120px]">
                <div className="text-3xl font-bold text-white">
                  {teamStats?.home_record}
                </div>
                <div className="text-[#999999] mt-1">Home</div>
              </div>

              <div className="bg-[rgba(0,0,0,0.4)] backdrop-blur-sm rounded-[14px] px-6 py-3 min-w-[120px]">
                <div className="text-3xl font-bold text-white">
                  {teamStats?.away_record}
                </div>
                <div className="text-[#999999] mt-1">Away</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#111111] rounded-[14px] shadow-md p-6 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-[#999999] text-sm uppercase tracking-wide font-medium">
                Win %
              </h3>
              <p className="text-3xl font-bold mt-1 text-[#F5F5F5]">
                {/* {((stats.wins / (stats.wins + stats.losses)) * 100 || 0).toFixed(1)}% */}
                {teamStats?.overall_win_percentage?.toFixed(1) || 0}%
              </p>
            </div>
            <div className="p-3 bg-[#1A1A1A] rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-[#E8B800]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <div className="mt-4 w-full bg-[#1A1A1A] rounded-full h-2">
            <div
              className="themeSecondary h-2 rounded-full"
              style={{
                width: `${teamStats?.overall_win_percentage?.toFixed(1) || 0}%`,
                backgroundColor: teamPrimaryColor,
              }}
            ></div>
          </div>
        </div>

        <div className="bg-[#111111] rounded-[14px] shadow-md p-6 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-[#999999] text-sm uppercase tracking-wide font-medium">
                Conference
              </h3>
              <p className="text-3xl font-bold mt-1 text-[#F5F5F5]">
                {teamStats?.conference_record}
              </p>
            </div>
            <div className="p-3 bg-[#1A1A1A] rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-indigo-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                />
              </svg>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-[#555555]">Win %</span>
              <span className="font-medium text-[#F5F5F5]">
                {teamStats?.conference_win_percentage}%
              </span>
            </div>
            <div className="w-full bg-[#1A1A1A] rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full"
                style={{
                  width: `${teamStats?.conference_win_percentage}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-[#111111] rounded-[14px] shadow-md p-6 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-[#999999] text-sm uppercase tracking-wide font-medium">
                Current Streak
              </h3>
              <p className="text-3xl font-bold mt-1 text-[#F5F5F5]">
                {teamStats?.current_streak}
              </p>
            </div>
            <div className="p-3 bg-[#1A1A1A] rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center">
              {lastFiveGame?.slice(0, 5)?.map((game, index) => (
                <div
                  key={index}
                  className={`w-6 h-6 mx-1 flex items-center justify-center rounded-full text-xs font-bold ${
                    game.isWin
                      ? "themePrimary text-white"
                      : "bg-red-500 text-white"
                  }`}
                  title={`${game.isWin ? "Win" : "Loss"} vs ${game.opponent} (${
                    game.teamScore
                  }-${game.opponentScore})`}
                >
                  {game.isWin ? "W" : "L"}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#111111] rounded-[14px] shadow-md p-6 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-[#999999] text-sm uppercase tracking-wide font-medium">
                Next Game
              </h3>
              {stats.upcomingGames.length > 0 ? (
                <p className="text-xl font-bold mt-1 truncate text-[#F5F5F5]">
                  vs. {stats.upcomingGames[0].opponent}
                </p>
              ) : (
                <p className="text-xl font-bold mt-1 text-[#F5F5F5]">None Scheduled</p>
              )}
            </div>
            <div className="p-3 bg-[#1A1A1A] rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-[#E8B800]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          {stats.upcomingGames.length > 0 && (
            <div className="mt-2">
              <div className="text-sm text-[#999999]">
                {formatDate(stats.upcomingGames[0].date)}
                {stats.upcomingGames[0].time &&
                  ` • ${stats.upcomingGames[0].time}`}
              </div>
              <div className="text-xs mt-1 text-[#555555]">
                {stats.upcomingGames[0].isHome ? "Home" : "Away"}
                {stats.upcomingGames[0].venue &&
                  ` • ${stats.upcomingGames[0].venue}`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Last 5 Games */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-2">
          <div className="bg-[#111111] rounded-[14px] shadow-md overflow-hidden">
            <div className="bg-[#0A0A0A] px-6 py-4 border-b border-[#1A1A1A] flex justify-between items-center">
              <h2 className="text-[#F5F5F5] text-xl font-bold">Last 5 Games</h2>
              <span className="text-sm text-[#555555]">
                {stats.wins}-{stats.losses} Overall
              </span>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {lastFiveGame.map((game, index) => (
                  <div
                    key={game.gameId || index}
                    className="flex flex-col sm:flex-row sm:items-center border-b border-[#1A1A1A] pb-4"
                  >
                    <div className="flex-1 flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          game.isWin
                            ? "themePrimary"
                            : "bg-red-500"
                        }`}
                      >
                        {game.isWin ? "W" : "L"}
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <span className="text-[#F5F5F5] font-medium">
                            {game.isHome ? "vs" : "@"} {game.opponent}
                          </span>
                          {game.conference && (
                            <span className="ml-2 px-2 py-0.5 bg-[#1A1A1A] text-[#E8B800] text-xs rounded">
                              {game.conference}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[#555555]">
                          {formatDate(game.date)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 sm:mt-0 sm:text-right">
                      <div className="text-lg font-bold text-[#F5F5F5]">
                        {game.teamScore} - {game.opponentScore}
                      </div>
                      <div className="text-xs text-[#555555]">
                        {game.isHome ? "Home" : "Away"}
                      </div>
                    </div>
                  </div>
                ))}

                {lastFiveGame.length === 0 && (
                  <div className="text-center py-8 text-[#555555]">
                    No recent games found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-[#111111] rounded-[14px] shadow-md overflow-hidden h-full">
            <div className="bg-[#0A0A0A] px-6 py-4 border-b border-[#1A1A1A]">
              <h2 className="text-[#F5F5F5] text-xl font-bold">
                Upcoming Games
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {stats.upcomingGames.map((game, index) => (
                  <div
                    key={game.gameId || index}
                    className="flex flex-col border-b border-[#1A1A1A] pb-6 last:border-0 last:pb-0"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-[#F5F5F5] font-medium">
                          {game.isHome ? "vs" : "@"} {game.opponent}
                        </span>
                        {game.conference && (
                          <span className="ml-2 px-2 py-0.5 bg-[#1A1A1A] text-[#E8B800] text-xs rounded">
                            {game.conference}
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1 bg-[#1A1A1A] text-[#E8B800] text-xs rounded">
                        {game.isHome ? "Home" : "Away"}
                      </div>
                    </div>
                    <div className="flex items-center mt-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-[#555555] mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm text-[#999999]">
                        {formatDate(game.date)}
                      </span>
                      {game.time && (
                        <span className="text-sm text-[#999999] ml-2">
                          • {game.time}
                        </span>
                      )}
                    </div>
                    {game.venue && (
                      <div className="flex items-center mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-[#555555] mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-sm text-[#555555]">
                          {game.venue}
                        </span>
                      </div>
                    )}
                  </div>
                ))}

                {stats.upcomingGames.length === 0 && (
                  <div className="text-center py-8 text-[#555555]">
                    No upcoming games scheduled
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Schedule Section */}
      <div className="bg-[#111111] rounded-[14px] shadow-md overflow-hidden mb-10">
        <div className="bg-[#0A0A0A] px-6 py-4 border-b border-[#1A1A1A]">
          <h2 className="text-[#F5F5F5] text-xl font-bold">Complete Schedule</h2>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-[#1A1A1A]">
          <div className="flex overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                activeTab === "all"
                  ? "border-b-2 border-[#E8B800] text-[#E8B800]"
                  : "text-[#999999] hover:text-[#F5F5F5] hover:border-[#555555]"
              }`}
            >
              All Games
            </button>
            <button
              onClick={() => setActiveTab("home")}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                activeTab === "home"
                  ? "border-b-2 border-[#E8B800] text-[#E8B800]"
                  : "text-[#999999] hover:text-[#F5F5F5] hover:border-[#555555]"
              }`}
            >
              Home
            </button>
            <button
              onClick={() => setActiveTab("away")}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                activeTab === "away"
                  ? "border-b-2 border-[#E8B800] text-[#E8B800]"
                  : "text-[#999999] hover:text-[#F5F5F5] hover:border-[#555555]"
              }`}
            >
              Away
            </button>
            <button
              onClick={() => setActiveTab("conference")}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                activeTab === "conference"
                  ? "border-b-2 border-[#E8B800] text-[#E8B800]"
                  : "text-[#999999] hover:text-[#F5F5F5] hover:border-[#555555]"
              }`}
            >
              Conference
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                activeTab === "completed"
                  ? "border-b-2 border-[#E8B800] text-[#E8B800]"
                  : "text-[#999999] hover:text-[#F5F5F5] hover:border-[#555555]"
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setActiveTab("upcoming")}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                activeTab === "upcoming"
                  ? "border-b-2 border-[#E8B800] text-[#E8B800]"
                  : "text-[#999999] hover:text-[#F5F5F5] hover:border-[#555555]"
              }`}
            >
              Upcoming
            </button>
          </div>
        </div>

        {/* Schedule Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#1A1A1A]">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#999999]"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#999999]"
                >
                  Opponent
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#999999]"
                >
                  Location
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#999999]"
                >
                  Conference
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#999999]"
                >
                  Result/Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#0A0A0A] divide-y divide-[#1A1A1A]">
              {filteredGames()?.map((game, index) => {
                const isHome =
                  parseInt(game.HomeID) ===
                  parseInt(teamId || team?.teamID || team?.team_id);
                const teamScore = isHome ? game.ScoreHome : game.ScoreVis;
                const opponentScore = isHome ? game.ScoreVis : game.ScoreHome;
                const opponent = isHome ? game.Visitor : game.Home;
                const location = isHome ? "Home" : "Away";
                const isWin =
                  game.GameStatus === "Final" && teamScore > opponentScore;
                const isLoss =
                  game.GameStatus === "Final" && teamScore < opponentScore;

                return (
                  <tr
                    key={game.ID || game.GameID || index}
                    className="hover:bg-[rgba(255,255,255,0.04)] cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedGameId(game.ID || game.GameID);
                      setIsStatSliderOpen(true);
                    }}
                    data-game-id={game.ID || game.GameID}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#999999]">
                      {formatDate(game.GameDay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-[#F5F5F5]">
                          {opponent}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isHome
                              ? "bg-green-900/30 text-green-400"
                              : "bg-indigo-900/30 text-indigo-400"
                          }`}
                        >
                          {location}
                        </span>
                        {game.Venue && (
                          <span className="ml-2 text-sm text-[#555555]">
                            {game.Venue}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#555555]">
                      {game.Conference || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span
                          className={`flex-shrink-0 inline-block h-4 w-4 rounded-full ${
                            isWin
                              ? "bg-green-400"
                              : isLoss
                              ? "bg-red-500"
                              : "bg-[#555555]"
                          }`}
                        ></span>
                        <span className="ml-2 text-sm font-medium text-[#F5F5F5]">
                          {game.GameStatus === "Final" ? (
                            <>
                              {isWin ? "W" : "L"} {teamScore}-{opponentScore}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 ml-2 text-[#E8B800]"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-[#E8B800]/20 text-[#E8B800]">
                              {game.GameTime || "TBD"}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredGames().length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-[#555555]"
                  >
                    No games found matching the selected filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex justify-center pb-5">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalTeams}
            onChange={handlePageChange}
            showSizeChanger
            pageSizeOptions={[5, 10, 20, 50]}
            isDarkMode={true}
          />
        </div>
      </div>

      {/* Game Stats Slider */}
      <GameStatSlider
        gameId={selectedGameId}
        teamId={teamId || team?.teamID || team?.team_id}
        isOpen={isStatSliderOpen}
        onClose={() => setIsStatSliderOpen(false)}
      />
    </div>
  );
};

export default TeamRecord;
